import React, { memo, useCallback, useMemo } from 'react';
import { TextField, Autocomplete } from '@mui/material';
import { makeStyles } from '@mui/styles';

import Popper from './Popper';
import CheckmarkIcon from '../icons/Checkmark';

const useStyles = makeStyles(({ typography }) => {
  const { pxToRem } = typography;
  return {
    input: {
      padding: 0,
    },
    option: {
      backgroundColor: '#fff',
    },
    menuOptionContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    },
    optionLabel: {
      marginRight: pxToRem(10),
    },
  };
});

const CustomAutocomplete = ({
  options,
  onChange,
  label,
  variant,
  value,
  popperProps,
  disabled,
  groupBy,
  dataTestId,
  renderInput,
  placeholder,
  renderOptionLabel,
  ...autocompleteProps
}) => {
  const style = useStyles();

  const handleChangeOption = useCallback(
    (e, newOption) => {
      onChange(newOption?.value);
    },
    [onChange]
  );

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) || null,
    [options, value]
  );

  const getOptionLabel = useCallback((option) => option.label, []);

  const CustomPopper = (props) => <Popper {...props} {...popperProps} />;

  const handleRenderInput = useCallback(
    (params) =>
      renderInput ? (
        renderInput(params)
      ) : (
        <TextField
          {...params}
          inputProps={{
            ...params.inputProps,
            className: style.input,
          }}
          InputLabelProps={{
            ...params.InputLabelProps,
          }}
          label={label}
          placeholder={placeholder}
          variant={variant}
        />
      ),
    [renderInput, style.input, label, placeholder, variant]
  );

  const renderOption = useCallback(
    (props, option) => (
      <div className={style.menuOptionContainer} key={option.value} {...props}>
        {renderOptionLabel ? (
          renderOptionLabel(option)
        ) : (
          <span className={style.optionLabel}>{option.label}</span>
        )}
        {selectedOption && option.value === selectedOption.value && (
          <CheckmarkIcon />
        )}
      </div>
    ),
    [selectedOption, style]
  );

  return (
    <Autocomplete
      options={options}
      size="small"
      disabled={disabled}
      groupBy={groupBy}
      data-testid={dataTestId}
      PopperComponent={CustomPopper}
      value={selectedOption}
      getOptionLabel={getOptionLabel}
      onChange={handleChangeOption}
      renderInput={handleRenderInput}
      renderOption={renderOption}
      {...autocompleteProps}
    />
  );
};

export default memo(CustomAutocomplete);
